import styled from '@emotion/styled';
import { Box } from '@mui/material';
import { Stack } from '@mui/system';
import { GridActionsCellItem, GridEditInputCell } from '@mui/x-data-grid';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectInvoice,
  selectInvoiceValidation,
  deleteItemValidation,
  setItemFieldValidation,
  setItemValidation,
  updateInvoice,
  doRecalculateInvoiceAsync,
} from './correctionInvoiceEditorSlice';
import { getVatValue, createVatDefinition } from '../../utility/vat';
import React, { useState } from 'react';
import UnitSelect from '../../components/unitsSelect';
import VatSelector from '../../components/vatSelector';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { nanoid } from '@reduxjs/toolkit';
import NoRowsOverlayWithAdd from '../../components/noRowsOverlay/customNoRowsOverlayWithAdd';
import ShortCorrectionInvoiceSummaryPanel from './shortCorrectionInvoiceSummary';
import { formatNumber } from 'accounting-js';
import MDButton from 'components/MDButton';
import BillzTypographyGridHeader from '../../components/BillzTypographies/BillzTypographyGridHeader';
import BillzTypographySubTitle from 'components/BillzTypographies/BillzTypographySubTitle';
import BillzTypography from 'components/BillzTypographies/BillzTypography';
import { StatusButton } from './StatusButton/StatusButton';
import CorrectionDataGrid from './CorrectionDataGrid/CorrectionDataGrid';
import CellInput from './CellInput/CellInput';
import { DeleteListItemConfirmationModal } from 'components/DeleteListItemConfirmationModal/DeleteListItemConfirmationModal';
import ProductSearchControl from 'components/ProductSearchControl/ProductSearchControl';
import { toNumber } from 'lodash';
import { OEM } from 'utility/OEMProvider';

const UNIT_PRICE_PRECISION = 4;
const FLOAT_PRECISION = 2;

const StyledBox = styled(Box)(({ theme }) => ({
  width: '100%',
  '& .MuiDataGrid-cell--editable': {
    '& .MuiInputBase-root': {
      height: '100%',
    },
  },
  '& .Mui-no-error': {},
  '& .Mui-error': {
    backgroundColor: `rgb(126,10,15, ${theme.palette.mode === 'dark' ? 0 : 0.1})`,
    color: theme.palette.mode === 'dark' ? '#ff4343' : '#750f0f',
  },
}));

export default function CorrectionItemsPanel(props) {
  const dispatch = useDispatch();
  const invoice = useSelector(selectInvoice);

  const invoiceValidation = useSelector(selectInvoiceValidation);

  const [isReadOnly, updateIsReadOnly] = useState(props.isReadOnly);
  const [deleteDialogConfirmationOpen, setDeleteDialogConfirmationOpen] = React.useState(false);
  const [deleteRowId, setDeleteRowId] = React.useState(null);

  const buildPayload = (id, value, param) => {
    return {
      type: id,
      value: value,
      param: param,
    };
  };

  // #region click event handlers

  const handleDeleteRowDisagreed = () => {
    setDeleteRowId(null);
    setDeleteDialogConfirmationOpen(false);
  };

  const handleDeleteRowAgreed = () => {  
    let items = [...invoice.items];

    items = items.map(item => {
      if(item.no === deleteRowId && item.type === 'CORRECTED') {
        return { ...item, quantity: 0 };
      } else {
        return item;
      }
    });
    
    dispatch(doRecalculateInvoiceAsync(buildPayload('items', items)));
    setDeleteRowId(null);
    setDeleteDialogConfirmationOpen(false);
  };  

  const handleDeleteRow = (id) => {
    var no = invoice.items.find((item) => item.id === id).no;
    dispatch(
      updateInvoice(
        buildPayload(
          'items',
          invoice.items.filter((item) => item.no !== no),
        ),
      ),
    );
    dispatch(deleteItemValidation(no));
  };

  const handleDeleteClick = (id, type) => () => {   
    if(type !== 'ADDED') {
      setDeleteRowId(id);      
      setDeleteDialogConfirmationOpen(true);
    } else {
      handleDeleteRow(id);
    }
  };

  const onAddNewInvoiceItemClick = () => {
    const items = invoice.items.slice();
    const newItem = createNewItemRow(items);
    items.push(newItem);
    dispatch(updateInvoice(buildPayload('items', items)));
    dispatch(setItemValidation(createNewItemValidationRow(newItem.id)));
  };

  //#endregion

  //#region value getters

  function getItemIndex(index) {
    if (index.row.type === 'ORIGINAL' || index.row.type === 'ADDED') {
      return index.row.no;
    }
  }

  function getType(params) {
    switch (params.row.type) {
      case 'ORIGINAL':
        return 'Oryginał';
      case 'CORRECTION':
        return 'Korekta';
      case 'CORRECTED':
        return 'Skorygowany';
      case 'ADDED':
        return 'Dodany';
    }
  }

  // #endregion

  // #region value setters

  function onQuantityValueSet(params) {
    const quantity = params.value;
    const unitNetPrice = params.row.unitNetPrice;
    let netValue = unitNetPrice === null ? null : (unitNetPrice * quantity).toFixed(FLOAT_PRECISION);
    let grossValue = unitNetPrice === null ? null : (netValue * params.row.vat.value).toFixed(FLOAT_PRECISION);

    return { ...params.row, quantity, netValue, grossValue };
  }

  function onUnitPriceValueSet(params) {
    const unitNetPrice = params.value;
    const quantity = params.row.quantity;
    let netValue = unitNetPrice === null ? null : (unitNetPrice * quantity).toFixed(FLOAT_PRECISION);
    let grossValue = unitNetPrice === null ? null : (netValue * params.row.vat.value).toFixed(FLOAT_PRECISION);

    return { ...params.row, unitNetPrice, quantity, netValue, grossValue };
  }

  function onTaxRateValueSet(params) {
    let newTaxValue = getVatValue(params.value) ?? createVatDefinition(params.value).value;
    if (isNaN(newTaxValue)) {
      newTaxValue = 0;
    }
    const taxRate = (100 + newTaxValue) / 100;
    const quantity = params.row.quantity;
    const unitNetPrice = params.row.unitNetPrice;

    let netValue = unitNetPrice === null ? null : (unitNetPrice * quantity).toFixed(FLOAT_PRECISION);
    let grossValue = unitNetPrice === null ? null : (netValue * taxRate).toFixed(FLOAT_PRECISION);

    let vat = {
      name: params.value ? params.value.toString() : '0',
      value: taxRate,
    };
    return { ...params.row, quantity, unitNetPrice, vat, netValue, grossValue };
  }

  function onNetPriceValueSet(params) {
    const netValue = params.value;
    const quantity = params.row.quantity;
    const taxRate = params.row.vat.value;

    let unitNetPrice = netValue === null ? null : (netValue / quantity).toFixed(FLOAT_PRECISION);
    let grossValue = netValue === null ? null : (netValue * taxRate).toFixed(FLOAT_PRECISION);

    let vat = {
      name: params.row.vat.name,
      value: taxRate,
    };

    return { ...params.row, quantity, unitNetPrice, vat, netValue, grossValue };
  }

  function onGrossPriceValueSet(params) {
    const grossValue = params.value;
    const taxRate = params.row.vat.value;
    const quantity = params.row.quantity;

    let netValue = grossValue === null ? null : (grossValue / taxRate).toFixed(FLOAT_PRECISION);
    let unitNetPrice = grossValue === null ? null : (netValue / quantity).toFixed(FLOAT_PRECISION);

    let vat = {
      name: (taxRate * 100 - 100).toString(),
      value: taxRate,
    };

    return { ...params.row, quantity, unitNetPrice, vat, netValue, grossValue };
  }

  //#endregion

  // #region value formatters

  function onFormatWithPrecision(params) {
    if (params.value == null) {
      return '';
    }

    return formatNumber(params.value, { precision: FLOAT_PRECISION, thousand: ' ', decimal: ',' });
  }

  // #endregion

  // #region validation

  function stringNotEmptyValidator(value) {
    return value !== null && value != undefined && value.trim().length > 0;
  }

  function numberNotEmptyValidator(value) {
    return value !== null && value !== undefined;
  }

  function validateRowValues(row) {
    validateCellValue(row.id, 'name', row.name, stringNotEmptyValidator);
    validateCellValue(row.id, 'quantity', row.quantity, numberNotEmptyValidator);
    validateCellValue(row.id, 'unit', row.unit, stringNotEmptyValidator);
    validateCellValue(row.id, 'unitNetPrice', row.unitNetPrice, numberNotEmptyValidator);
    validateCellValue(row.id, 'vat', row.vat.value, numberNotEmptyValidator);
    validateCellValue(row.id, 'netValue', row.netValue, numberNotEmptyValidator);
    validateCellValue(row.id, 'grossValue', row.grossValue, numberNotEmptyValidator);
  }

  function validateCellValue(rowId, field, newValue, validator) {
    if (!Array.isArray(invoiceValidation.items)) {
      return true;
    } // validation not yet loaded (initial state)

    let item = invoiceValidation.items.find((item) => item.id === rowId);
    console.log(JSON.stringify(item));
    const valid = validator(newValue);
    if (!valid && (item === undefined || item[field] === undefined)) {
      return true;
    }

    dispatch(setItemFieldValidation({ id: rowId, field: field, valid: valid }));
    return valid;
  }

  function preProcessEditCellProps(params, field, validator) {
    const valid = validateCellValue(params.id, field, params.props.value, validator);
    return { ...params.props, error: !valid };
  }

  function cellClassName(params, field) {
    if (!Array.isArray(invoiceValidation.items)) {
      return true;
    } // validation not yet loaded (initial state)
    let item = invoiceValidation.items.find((item) => item.id === params.id);
    if (item === undefined) {
      return 'Mui-no-error';
    }
    return item[field] === false ? 'Mui-error' : 'Mui-no-error';
  }

  const renderColHeader = (params) => (
    <BillzTypographyGridHeader color="text">{params.colDef.headerName}</BillzTypographyGridHeader>
  );

  // #endregion

  const isCellEditable = (type, field) => {
    if(type === 'CORRECTION' || type === 'ORIGINAL') {
      return false;
    } else if(type === 'ADDED') {
      return true;
    } else {
      switch(field) {
        case 'quantity': return true;
        case 'unitNetPrice': return true;
        case 'vat': return true;
        case 'netValue': return true;
        default: return false;
      }
    }
  };

  const onProductDataSet = (rowId, productId, data) => {
    const items = invoice.items.slice();
    const rowToUpdateIndex = items.findIndex((x) => x.id === rowId);
    const newData = {...items[rowToUpdateIndex]};
    newData.productId = productId;
    newData.name = data.name;
    newData.unit = data.unit;
    newData.pkwiu = data.pkWiU;

    if(data.unitNetPrice === undefined ||  data.unitNetPrice === null) {
      newData.unitNetPrice = undefined;
      newData.netValue = undefined;
      newData.grossValue = undefined;
    } else {
      newData.unitNetPrice = toNumber(data.unitNetPrice);
      newData.netValue = (newData.unitNetPrice * newData.quantity).toFixed(FLOAT_PRECISION);
      newData.grossValue = (newData.netValue * newData.vat.value).toFixed(FLOAT_PRECISION);
    }

    onProcessRowUpdate(newData);
  }

  const invoiceItemsColumns = [
    {
      field: 'index',
      headerName: 'ID',
      filterable: false,
      sortable: false,
      width: 15,
      valueGetter: getItemIndex,
      renderHeader: renderColHeader,
      renderCell: (params) => <BillzTypography variant="button">{params.value}</BillzTypography>,
    },
    {
      field: 'type',
      headerName: 'Status',
      flex: 1.5,
      filterable: false,
      sortable: false,
      width: 15,
      valueGetter: (params) => getType(params),
      renderHeader: renderColHeader,
      renderCell: (params) => (
        <StatusButton
          variant={params.row.type}
          {...params}
          InputProps={{ disableUnderline: true, readOnly: true }}
          align="right"
        >
          {getType(params)}
        </StatusButton>
      ),
    },
    {
      field: 'name',
      headerName: 'Nazwa',
      flex: 2,
      minWidth: 100,
      maxWidth: 350,
      filterable: false,
      sortable: false,
      type: 'string',
      editable:true,
      preProcessEditCellProps: (params) => {
        preProcessEditCellProps(params, 'name', stringNotEmptyValidator);
      },
      cellClassName: (params) => {
        return cellClassName(params, 'name');
      },
      renderHeader: renderColHeader,
      renderEditCell: (params) => {
        if(OEM().isInventoryEnabled && isCellEditable(params.row.type, 'name')) {
          return <ProductSearchControl {...params} onProductDataSet={onProductDataSet} />;
        }

        return <GridEditInputCell {...params} />;
      },
      renderCell: (params) => (
        <CellInput
          type={params.row.type}
          variant="standard"
          editable={isCellEditable(params.row.type, 'name')}
          {...params}
          InputProps={{ disableUnderline: true, readOnly: true }}
          align="right"
        />
      ),
    },

    {
      field: 'pkwiu',
      headerName: 'PKWiU',
      flex: 2,
      minWidth: 50,
      maxWidth: 100,
      editable: true,
      filterable: false,
      sortable: false,
      type: 'string',
      renderHeader: renderColHeader,
      renderCell: (params) => (
        <CellInput
          type={params.row.type}
          variant="standard"
          editable={isCellEditable(params.row.type, 'pkwiu')}
          {...params}
          InputProps={{ disableUnderline: true, readOnly: true }}
          align="right"
        />
      ),
    },  
    {
      field: 'quantity',
      headerName: 'Ilość',
      width: 130,
      filterable: false,
      sortable: false,
      type: 'number',
      editable:true,
      valueSetter: onQuantityValueSet,
      preProcessEditCellProps: (params) => {
        preProcessEditCellProps(params, 'quantity', numberNotEmptyValidator);
      },
      cellClassName: (params) => {
        return cellClassName(params, 'quantity');
      },
      renderHeader: renderColHeader,
      renderCell: (params) => (
        <CellInput
          type={params.row.type}
          variant="standard"
          editable={isCellEditable(params.row.type, 'quantity')}
          {...params}
          InputProps={{ disableUnderline: true, readOnly: true }}
          align="right"
        />
      ),
    },
    {
      field: 'unit',
      headerName: 'Jednostka',
      width: 100,
      filterable: false,
      sortable: false,
      editable:true,
      renderEditCell: (params) => <UnitSelect {...params} />,
      renderHeader: renderColHeader,
      preProcessEditCellProps: (params) => {
        preProcessEditCellProps(params, 'unit', stringNotEmptyValidator);
      },
      cellClassName: (params) => {
        return cellClassName(params, 'unit');
      },
      renderCell: (params) => (
        params.row.type !== 'CORRECTION' && (
          <CellInput
            type={params.row.type}
            variant="standard"
            editable={isCellEditable(params.row.type, 'unit')}
            {...params}
            InputProps={{ disableUnderline: true, readOnly: false }}
            align="right"
          />
        )
      ),
    },
    {
      field: 'unitNetPrice',
      headerName: 'Cena netto',
      flex: 1,
      minWidth: 100,
      maxWidth: 200,
      filterable: false,
      sortable: false,
      type: 'number',
      editable:true,
      valueSetter: onUnitPriceValueSet,
      valueFormatter: onFormatWithPrecision,
      renderHeader: renderColHeader,
      preProcessEditCellProps: (params) => {
        preProcessEditCellProps(params, 'unitNetPrice', numberNotEmptyValidator);
      },
      cellClassName: (params) => {
        return cellClassName(params, 'unitNetPrice');
      },
      renderCell: (params) => (
        <CellInput
          type={params.row.type}
          value={onFormatWithPrecision(params)}
          variant="standard"
          editable={isCellEditable(params.row.type, 'unitNetPrice')}
          InputProps={{ disableUnderline: true, readOnly: true }}
          align="right"
        />
      ),
    },
    {
      field: 'vat',
      headerName: 'VAT(%)',
      width: 80,
      filterable: false,
      sortable: false,
      type: 'string',
      editable:true,
      valueSetter: onTaxRateValueSet,
      valueGetter: (params) => params.row.vat.name,
      renderEditCell: (params) => <VatSelector {...params} />,
      renderHeader: renderColHeader,
      preProcessEditCellProps: (params) => {
        preProcessEditCellProps(params, 'vat', stringNotEmptyValidator);
      },
      cellClassName: (params) => {
        return cellClassName(params, 'vat');
      },
      renderCell: (params) => (
        params.row.type !== 'CORRECTION' && (
          <CellInput
            type={params.row.type}
            variant="standard"
            editable={isCellEditable(params.row.type, 'vat')}
            {...params}
            InputProps={{ disableUnderline: true, readOnly: true }}
            align="right"
          />
        )
      ),
    },
    {
      field: 'netValue',
      headerName: 'Wartość netto',
      flex: 1,
      minWidth: 100,
      maxWidth: 200,
      filterable: false,
      sortable: false,
      type: 'number',
      editable:true,
      valueSetter: onNetPriceValueSet,
      valueFormatter: onFormatWithPrecision,
      preProcessEditCellProps: (params) => {
        preProcessEditCellProps(params, 'netValue', numberNotEmptyValidator);
      },
      cellClassName: (params) => {
        return cellClassName(params, 'netValue');
      },
      renderHeader: renderColHeader,
      renderCell: (params) => (
        <CellInput
          type={params.row.type}
          value={onFormatWithPrecision(params)}
          variant="standard"
          editable={isCellEditable(params.row.type, 'netValue')}
          InputProps={{ disableUnderline: true, readOnly: true }}
          align="right"
        />
      ),
    },
    {
      field: 'grossValue',
      headerName: 'Wartość brutto',
      flex: 1,
      minWidth: 100,
      maxWidth: 200,
      filterable: false,
      sortable: false,
      editable:true,
      type: 'number',
      valueSetter: onGrossPriceValueSet,
      valueFormatter: onFormatWithPrecision,
      preProcessEditCellProps: (params) => {
        preProcessEditCellProps(params, 'grossValue', numberNotEmptyValidator);
      },
      cellClassName: (params) => {
        return cellClassName(params, 'grossValue');
      },
      renderHeader: renderColHeader,
      renderCell: (params) => (
        <CellInput
          type={params.row.type}
          value={onFormatWithPrecision(params)}
          variant="standard"
          editable={isCellEditable(params.row.type, 'grossValue')}
          InputProps={{ disableUnderline: true, readOnly: true }}
          align="right"
        />
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Akcje',
      width: 90,
      cellClassName: 'actions',
      renderHeader: renderColHeader,
      getActions: (params) => {
        return params.row.type == 'ORIGINAL' || params.row.type == 'ADDED'
          ? [
              <GridActionsCellItem
                icon={<DeleteIcon sx={{ color: '#ee0000' }} />}
                label="Usuń"
                onClick={handleDeleteClick(params.row.id, params.row.type)}
                color="inherit"
              />,
            ]
          : [<Stack />];
      },
    },
  ];

  const createNewItemRow = (items) => {
    var currentMaxNo = getCurrentMax(items);

    return {
      id: nanoid(),
      no: currentMaxNo + 1,
      name: '',
      quantity: 1,
      unit: 'szt',
      unitNetPrice: null,
      vat: {
        name: '23%',
        value: 1.23,
      },
      netValue: null,
      grossValue: null,
      type: 'ADDED',
    };
  };

  const getCurrentMax = (items) => {
    var currentMaxNo = 0;
    items.forEach((item) => {
      if (item.no > currentMaxNo) {
        currentMaxNo = item.no;
      }
    });
    return currentMaxNo;
  };

  const createNewItemValidationRow = (id) => {
    return {
      id: id,
      name: undefined,
      quantity: true,
      unit: true,
      unitNetPrice: undefined,
      vat: true,
      netValue: undefined,
      grossValue: undefined,
    };
  };

  const onProcessRowUpdate = (newData) => {
    const items = invoice.items.slice();
    const rowToUpdateIndex = items.findIndex((x) => x.id === newData.id);
    items[rowToUpdateIndex] = newData;

    validateRowValues(newData);
    dispatch(doRecalculateInvoiceAsync(buildPayload('items', items)));
    return newData;
  };

  return (
    <React.Fragment>
      {deleteDialogConfirmationOpen && (
        <DeleteListItemConfirmationModal
          id="delete-confirmation-dialog"
          onConfirm={handleDeleteRowAgreed}
          onClose={handleDeleteRowDisagreed}
          title={'Wykonać korektę całkowitą pozycji?'}
          confirmText={'Tak'}
          message={'Usuwanie pozycji korekty pochodzącej z oryginalnego dokumentu jest niedozwolone. Czy wykonać korektę całkowitą pozycji?'}
        />
      )}    
      <Stack id="line-items-grid">
        <BillzTypographySubTitle color="text" variant="body1" sx={{ py: 1 }}>
          Pozycje
        </BillzTypographySubTitle>
        <StyledBox sx={{ mt: 4 }}>
          <CorrectionDataGrid         
            sx={{ color: 'text.main' }}
            spacing={10}
            autoHeight={true}
            autoPageSize={false}
            rows={invoice.items}
            rowHeight={64}
            getRowHeight={() => 48}
            hideFooterPagination={true}
            hideFooter={true}
            columns={invoiceItemsColumns}
            disableIgnoreModificationsIfProcessingProps={true}
            disableSelectionOnClick
            disableColumnFilter
            disableColumnMenu
            experimentalFeatures={{ newEditingApi: true }}
            components={{
              NoRowsOverlay: NoRowsOverlayWithAdd,
            }}
            componentsProps={{
              noRowsOverlay: { onClick: onAddNewInvoiceItemClick },
            }}
            getRowClassName={(params) => `correction-grid-${params.row.type}`}
            processRowUpdate={onProcessRowUpdate}
            isCellEditable={(params) => isCellEditable(params.row.type, params.field)}
          />
        </StyledBox>
      </Stack>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={2}
        sx={{ width: '100%' }}
      >
        <MDButton
          color="info"
          variant="text"
          id="add-line-item"
          size="small"
          disabled={isReadOnly}
          onClick={onAddNewInvoiceItemClick}
        >
          + Dodaj pozycję
        </MDButton>
        <ShortCorrectionInvoiceSummaryPanel isReadOnly={isReadOnly} />
      </Stack>
    </React.Fragment>
  );
}
